/* ==========================================================================
   motion-core.js :: Ordin Daylight Clone (Phase C)

   Owns: html.js-motion gating, motion gates, Lenis smooth scroll (desktop
   fine-pointer only), GSAP/ScrollTrigger wiring, the IO reveal engine
   (MOTION-CONTRACT in motion.css), the data-countup stat engine, and
   ScrollTrigger refresh hooks.

   Gates:
     MOTION_OK = !prefers-reduced-motion && !Shopify.designMode
     LENIS_OK  = MOTION_OK && (pointer: fine)

   Public API (stable; cart.js/pdp.js/global.js consume it):
     window.Ordin = { lenis: Lenis|null, motionOK: boolean }
   Consumers must stay null-safe on Ordin.lenis (mobile/reduced-motion = null).
   ========================================================================== */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var MOTION_OK = !reduced && !designMode;
  /* 2026-07-29: smooth (inertial) scrolling DISABLED per founder — native
     scroll on both axes. Lenis is still loaded but never instantiated, so
     window.Ordin.lenis stays null and every consumer falls back to native
     scroll. GSAP ScrollTrigger scrubs hook native scroll directly, so the
     home horizontal walk + pins still animate, just tracking the raw
     scrollbar 1:1 instead of Lenis's smoothed value. To re-enable, restore:
       var LENIS_OK = MOTION_OK && window.matchMedia('(pointer: fine)').matches; */
  var LENIS_OK = false;

  window.Ordin = window.Ordin || {};
  window.Ordin.motionOK = MOTION_OK;
  window.Ordin.lenis = null;

  /* Under reduced motion or the theme editor we never add the gating class:
     every motion.css initial-hidden state stays fully visible (contract #3/#5). */
  if (MOTION_OK) docEl.classList.add('js-motion');

  /* ----- GSAP / ScrollTrigger wiring -------------------------------------- */

  var hasGsap = !!(window.gsap && window.ScrollTrigger);
  if (hasGsap) window.gsap.registerPlugin(window.ScrollTrigger);

  /* ----- Lenis (desktop only; native touch scroll on mobile) -------------- */

  if (LENIS_OK && typeof window.Lenis === 'function') {
    var lenis = new window.Lenis();
    window.Ordin.lenis = lenis;

    if (hasGsap) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      window.requestAnimationFrame(function raf(time) {
        lenis.raf(time);
        window.requestAnimationFrame(raf);
      });
    }
  }

  /* ----- IO reveal engine (contract: motion.css) ---------------------------
     One observer, threshold 0.15, reveal once then unobserve.
     [data-reveal-stagger] parents are observed as a unit: children get
     --rd by DOM index, parent + children flip .is-revealed together
     (covers both .w word spans and [data-reveal] card children). */

  var observer = null;

  function revealNow(el) {
    el.classList.add('is-revealed');
    if (el.hasAttribute('data-reveal-stagger')) {
      el.querySelectorAll('[data-reveal]').forEach(function (child) {
        child.classList.add('is-revealed');
      });
    }
  }

  function prepare(el) {
    if (el.hasAttribute('data-reveal-delay')) {
      el.style.setProperty('--rd', parseInt(el.getAttribute('data-reveal-delay'), 10) + 'ms');
    }
    if (el.hasAttribute('data-reveal-duration')) {
      el.style.setProperty('--rdur', parseInt(el.getAttribute('data-reveal-duration'), 10) + 'ms');
    }
  }

  function prepareStagger(parent) {
    var step = parseInt(parent.getAttribute('data-reveal-stagger'), 10);
    if (isNaN(step)) step = 80;
    parent.style.setProperty('--stagger', step + 'ms');
    parent.querySelectorAll('[data-reveal]').forEach(function (child, i) {
      if (!child.hasAttribute('data-reveal-delay')) {
        child.style.setProperty('--rd', (i * step) + 'ms');
      }
    });
  }

  function scan(root) {
    if (!MOTION_OK) return;
    root = root || document;

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealNow(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.15 });
    }

    root.querySelectorAll('[data-reveal-stagger]').forEach(function (parent) {
      if (parent.classList.contains('is-revealed')) return;
      prepareStagger(parent);
      observer.observe(parent);
    });

    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      if (el.classList.contains('is-revealed')) return;
      prepare(el);
      /* Children of a stagger parent reveal with the parent. */
      if (el.closest('[data-reveal-stagger]')) return;
      observer.observe(el);
    });
  }

  scan(document);

  /* ----- count-up stat numerals (data-countup, 2026-07-30) ------------------
     Stat numerals count 0 -> final over ~1.2s (cubic ease-out) the first
     time ~40% of the element enters the viewport. The server-rendered text
     is the source of truth: we parse it once, animate only a plain
     comma-less integer (optional non-digit prefix/suffix preserved
     verbatim), and write the exact original string back on the last frame.
     Anything unparseable (decimals, thousands separators, multiple number
     groups) is left untouched. Gated by MOTION_OK + IO support, so reduced
     motion / theme editor / no-JS always show the final value immediately. */

  /* 2026-08-04 — two reliability fixes after "sometimes the numbers don't
     animate" on screen recordings:

     1. TRIGGER. The old observer used `threshold: 0.4` with no rootMargin.
        IntersectionObserver samples per frame, so a fast flick that carries
        the numeral from below the fold to above it between two frames never
        records a >=0.4 ratio and the count silently never fires. Now it
        triggers on ANY intersection, and a rAF-throttled scroll fallback
        sweeps up anything the observer still misses (it detaches itself once
        every counter has run).
     2. VISIBILITY. These numerals sit inside a [data-reveal] wrapper that
        fades in over 0.6s AFTER its own --rd delay. Both engines fired at
        roughly the same scroll position, so more than half of the 1.2s count
        played at opacity 0 — the recording shows a number that just appears.
        The count now waits for the wrapper to be revealed and for its delay
        to have elapsed, so the whole climb happens in plain sight. */

  var countObserver = null;
  var COUNT_RE = /^(\D*)(\d+)(\D*)$/;
  var COUNT_DUR = 2800; /* 2026-10-23: longer, so the slow landing reads */
  var countPending = [];
  var countScrollBound = false;

  function runCountup(el) {
    if (el.__countMates) { var mm = el.__countMates; el.__countMates = null; mm.forEach(function (m) { runCountup(m); }); }
    var finalText = el.__countFinal;
    var prefix = el.__countPrefix;
    var suffix = el.__countSuffix;
    var target = el.__countTarget;
    var start = null;

    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / COUNT_DUR, 1);
      /* 2026-10-23 (founder): speed ramp -- races up, then eases into the final number (expo-out) */
      /* expo-out with a softer last stretch (countUp.js-style): ~90% of the climb in the first third, then it settles */
      var eased = t === 1 ? 1 : 1 - Math.pow(2, -9 * t);
      if (t < 1) {
        el.textContent = prefix + Math.round(eased * target) + suffix;
        window.requestAnimationFrame(frame);
      } else {
        el.textContent = finalText; /* exact server-rendered string */
      }
    }
    window.requestAnimationFrame(frame);
  }

  /* Hold the count until its reveal wrapper is actually painting, so the
     climb is seen rather than played behind an opacity-0 fade. */
  function startCountup(el) {
    if (el.__countStarted) return;
    /* 2026-10-23 (founder): every number in a section lands at the SAME moment -- when one scrolls in, its siblings start
       with it (same start frame, same duration), so 43 / 64 / 76 all finish together. */
    var grp = el.closest ? el.closest('section, .shopify-section') : null;
    if (grp && !el.__countGroupLead) {
      var mates = Array.prototype.filter.call(grp.querySelectorAll('[data-countup]'), function (m) { return m !== el && !m.__countStarted && m.__countTarget != null; });
      el.__countGroupLead = true;
      el.__countMates = mates;
      mates.forEach(function (m) { m.__countStarted = true; var at = countPending.indexOf(m); if (at > -1) countPending.splice(at, 1); if (countObserver) countObserver.unobserve(m); });
    }
    el.__countStarted = true;

    var wrap = el.closest ? el.closest('[data-reveal], [data-es-reveal]') : null;
    if (!wrap) { runCountup(el); return; }

    var delay = parseInt(wrap.getAttribute('data-reveal-delay'), 10);
    if (isNaN(delay)) delay = 0;

    var waited = 0;
    function whenRevealed() {
      if (wrap.classList.contains('is-revealed') || waited > 2000) {
        window.setTimeout(function () { runCountup(el); }, delay + 80);
        return;
      }
      waited += 50;
      window.setTimeout(whenRevealed, 50);
    }
    whenRevealed();
  }

  function sweepPending() {
    for (var i = countPending.length - 1; i >= 0; i--) {
      var el = countPending[i];
      /* 2026-10-26 (QA S1 pageerror): startCountup can drop entries from countPending while this loop runs, which left
         i pointing past the end (el undefined -> TypeError). Skip holes. */
      if (!el || !el.getBoundingClientRect) continue;
      var r = el.getBoundingClientRect();
      /* anything at or above the fold has been seen (or scrolled past) */
      if (r.top < window.innerHeight && r.bottom > -window.innerHeight) {
        countPending.splice(i, 1);
        if (countObserver) countObserver.unobserve(el);
        startCountup(el);
      } else if (r.bottom <= -window.innerHeight) {
        /* flicked far past it: settle on the final value, no animation */
        countPending.splice(i, 1);
        if (countObserver) countObserver.unobserve(el);
        el.__countStarted = true;
      }
    }
    if (!countPending.length && countScrollBound) {
      window.removeEventListener('scroll', onCountScroll);
      countScrollBound = false;
    }
  }

  var countScrollRaf = null;
  function onCountScroll() {
    if (countScrollRaf) return;
    countScrollRaf = window.requestAnimationFrame(function () {
      countScrollRaf = null;
      sweepPending();
    });
  }

  function scanCountups(root) {
    if (!MOTION_OK || !('IntersectionObserver' in window)) return;

    if (!countObserver) {
      countObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countObserver.unobserve(entry.target);
          var at = countPending.indexOf(entry.target);
          if (at > -1) countPending.splice(at, 1);
          startCountup(entry.target);
        });
      }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    }

    (root || document).querySelectorAll('[data-countup]').forEach(function (el) {
      if (el.__countObserved) return;
      el.__countObserved = true;
      var text = (el.textContent || '').trim();
      var m = COUNT_RE.exec(text);
      if (!m) return; /* not a plain integer: leave the static text alone */
      el.__countFinal = text;
      el.__countPrefix = m[1];
      el.__countTarget = parseInt(m[2], 10);
      el.__countSuffix = m[3];
      countPending.push(el);
      countObserver.observe(el);
    });

    if (countPending.length && !countScrollBound) {
      window.addEventListener('scroll', onCountScroll, { passive: true });
      countScrollBound = true;
    }
  }

  scanCountups(document);

  /* ----- shadow overlay dim counter (Phase M) -------------------------------
     Counts [data-shadow-dim] sections currently holding the viewport center
     (middle 10% band via rootMargin) and toggles .shadow-overlay--dimmed on
     the fixed overlay (400ms opacity transition in motion.css).
     data-shadow-dim="scrub" sections are SKIPPED: the amber timeline
     scrub-fades the overlay texture itself across stages 3 -> 4 (blanket
     dimming at section entry would kill the overlay over stages 1-2). */

  var dimObserver = null;

  function initShadowDim() {
    var overlay = document.querySelector('[data-shadow-overlay]');
    if (!overlay || !MOTION_OK || !('IntersectionObserver' in window)) return;

    var dimCount = 0;

    dimObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var was = entry.target.__shadowDim === true;
        if (entry.isIntersecting && !was) dimCount++;
        if (!entry.isIntersecting && was) dimCount--;
        entry.target.__shadowDim = entry.isIntersecting;
      });
      overlay.classList.toggle('shadow-overlay--dimmed', dimCount > 0);
    /* -2px horizontal: an element parked at exactly translateX(100%) (the
       amber night panel) edge-touches the viewport and would count as
       intersecting; shrinking the band excludes zero-area touches. */
    }, { rootMargin: '-45% -2px -45% -2px', threshold: 0 });

    scanShadowDim(document);
  }

  function scanShadowDim(root) {
    if (!dimObserver) return;
    (root || document).querySelectorAll('[data-shadow-dim]').forEach(function (el) {
      if (el.getAttribute('data-shadow-dim') === 'scrub') return;
      if (el.__shadowDimObserved) return;
      el.__shadowDimObserved = true;
      dimObserver.observe(el);
    });
  }

  initShadowDim();

  /* ----- refresh + editor hooks -------------------------------------------- */

  function refresh() {
    if (hasGsap) window.ScrollTrigger.refresh();
  }

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  window.addEventListener('load', refresh);

  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
    scanCountups(e.target || document);
    scanShadowDim(e.target || document);
    refresh();
  });
})();
